import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, Divider } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import { useTheme } from "@mui/material/styles";

import { useCreatePostCategory } from "../../../hooks/blog/categories/useCreatePostCategory";
import { useUpdatePostCategory } from "../../../hooks/blog/categories/useUpdatePostCategory";
import { useGetPostCategories } from "../../../hooks/blog/categories/useGetPostCategories";
import FormInput from "../../inputs/FormInput";
import LanguageToggle from "../../global/LanguageToggle";
import { slugify } from "../../../utils/slugify";

const FormAddEditPostCategory = ({ currentData, isEdit = false }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";

    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Fields
    const [nameAr, setNameAr] = useState("");
    const [nameEn, setNameEn] = useState("");
    const [slug, setSlug] = useState("");
    const [parentId, setParentId] = useState("");

    // Mutations
    const { createCategoryMutate, isCreating } = useCreatePostCategory();
    const { updateCategoryMutate, isUpdating } = useUpdatePostCategory();

    // Fetch Potential Parents
    const { categories: allCategories } = useGetPostCategories({ per_page: 100 }); // Get all for parent selection
    const parentOptions = allCategories
        .filter(c => c.id !== currentData?.id) // Prevent self-parenting
        .map(c => ({ id: c.id, name: { ar: c.name?.ar, en: c.name?.en } }));

    useEffect(() => {
        if (isEdit && currentData) {
            setNameAr(currentData.name?.ar || "");
            setNameEn(currentData.name?.en || "");
            setSlug(currentData.slug || "");
            setParentId(currentData.parent_id || "");
        }
    }, [isEdit, currentData]);

    // Auto-generate slug
    useEffect(() => {
        if (nameEn && !isEdit) {
            setSlug(slugify(nameEn));
        }
    }, [nameEn, isEdit]);

    const SubmitForm = (e) => {
        e.preventDefault();

        const payload = {
            name: {
                en: nameEn || nameAr,
                ar: nameAr || nameEn
            },
            slug,
            parent_id: parentId || null,
        };

        if (isEdit) {
            updateCategoryMutate({ id: currentData.id, data: payload });
        } else {
            createCategoryMutate(payload);
        }
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2, md: 4 },
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "rgba(255,255,255,0.02)" : "#FFFFFF",
                borderRadius: 4,
                border: `1px solid ${theme.palette.divider}`,
            }}
        >
            <Box component="form" onSubmit={SubmitForm} sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Box>
                        <Typography variant="h5" fontWeight="bold" color="primary">
                            {isEdit
                                ? (isArabic ? "تعديل التصنيف" : "Edit Category")
                                : (isArabic ? "إضافة تصنيف جديد" : "Add New Category")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {isArabic ? "أدخل تفاصيل التصنيف" : "Enter category details"}
                        </Typography>
                    </Box>
                    <LanguageToggle activeLang={activeLang} onChange={(lng) => setActiveLang(lng)} showLabel={true} />
                </Box>

                <Grid container spacing={4}>
                    {/* Basic Info */}
                    <Grid item xs={12}>
                        <Typography variant="overline" color="text.secondary" fontWeight="bold">
                            {isArabic ? "البيانات الأساسية" : "Basic Information"}
                        </Typography>
                        <Divider sx={{ mb: 3, mt: 1 }} />
                    </Grid>

                    {/* Name */}
                    <Grid item xs={12} md={6}>
                        {activeLang === "ar" ? (
                            <FormInput
                                label="الاسم (عربي)"
                                placeholder="مثلاً: أخبار تقنية"
                                type="text"
                                value={nameAr}
                                onChange={(e) => setNameAr(e.target.value)}
                                required
                            />
                        ) : (
                            <FormInput
                                label="Name (English)"
                                placeholder="e.g., Tech News"
                                type="text"
                                value={nameEn}
                                onChange={(e) => setNameEn(e.target.value)}
                                required
                            />
                        )}
                    </Grid>

                    {/* Slug */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug)" : "Slug"}
                            value={slug}
                            onChange={(e) => setSlug(e.target.value)}
                            required
                            helperText={isArabic ? "يجب أن يكون فريداً" : "Must be unique"}
                            disabled={true} // Auto-generated
                        />
                    </Grid>

                    {/* Parent Category */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "التصنيف الأب" : "Parent Category"}
                            type="select"
                            name="parent_id"
                            value={parentId}
                            onChange={(e) => setParentId(e.target.value)}
                            options={parentOptions}
                        />
                    </Grid>

                </Grid>

                <Divider sx={{ my: 1 }} />

                <Box display="flex" justifyContent="flex-end">
                    <Button
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={isCreating || isUpdating}
                        sx={{
                            minWidth: 160,
                            borderRadius: '12px',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: theme.shadows[4],
                        }}
                    >
                        {isCreating || isUpdating ? (isArabic ? "جاري الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "إنشاء" : "Create"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditPostCategory;
